// ─── External slot contributions: GET /api/slots → slot registry (C3) ─────────────────
//
// The backend serves declarative UI-widget contributions (kind: ui-widget manifests) as plain,
// loosely-typed JSON. This adapter VALIDATES each one (slot ∈ SlotId, widget.kind ∈ vocabulary)
// and registers the survivors into the slot registry as mode-B (declarative) contributions. The
// host renders them through the sanitized WidgetView — a manifest never ships HTML/JS. Re-syncing
// clears only the external-namespaced contributions, so internal ones are untouched.

import { registerSlot, clearSlots } from './index'
import { WIDGET_KINDS } from './types'
import type { SlotId, SlotContribution, SlotCondition, WidgetSchema, PaneState } from './types'

/** Wire shape from GET /api/slots — slot/kind are strings until validated here. */
export interface SlotContributionDTO {
  id: string
  slot: string
  title: string
  icon?: string
  widget: { kind: string; [field: string]: unknown }
  when?: 'always' | { paneState: string } | { agent: string }
  order?: number
}

const SLOT_IDS: readonly string[] = [
  'composer-above', 'composer-tool', 'composer-inside',
  'panel', 'header', 'splitter', 'sidebar', 'overlay',
]
const PANE_STATES: readonly string[] = ['running', 'waiting', 'idle', 'error']

/** Namespace external ids so a re-sync clears only externally-sourced contributions. */
const EXTERNAL_PREFIX = 'ext:'

function coerceWhen(w: SlotContributionDTO['when']): SlotCondition | undefined {
  if (!w) return undefined
  if (w === 'always') return 'always'
  if ('paneState' in w) return PANE_STATES.includes(w.paneState) ? { paneState: w.paneState as PaneState } : undefined
  if ('agent' in w) return { agent: w.agent }
  return undefined
}

function toContribution(d: SlotContributionDTO): SlotContribution | null {
  if (!d || typeof d.id !== 'string' || typeof d.title !== 'string') return null
  if (!SLOT_IDS.includes(d.slot)) return null
  if (!d.widget || !WIDGET_KINDS.includes(d.widget.kind as (typeof WIDGET_KINDS)[number])) return null
  return {
    id: `${EXTERNAL_PREFIX}${d.id}`,
    slot: d.slot as SlotId,
    title: d.title,
    icon: d.icon,
    // kind is validated; WidgetView reads only the fields it knows per kind and escapes every
    // string, so trusting the remaining plain-data fields is safe (the XSS boundary is WidgetView).
    widget: d.widget as unknown as WidgetSchema,
    when: coerceWhen(d.when),
    order: typeof d.order === 'number' ? d.order : undefined,
  }
}

/** Replace every external slot contribution with a freshly-fetched set. Idempotent — safe to call
 *  on each app load / extension toggle without duplicating widgets. */
export function syncExternalSlots(dtos: SlotContributionDTO[]): void {
  clearSlots((c) => c.id.startsWith(EXTERNAL_PREFIX))
  for (const d of dtos) {
    const c = toContribution(d)
    if (c) registerSlot(c)
  }
}
